import type {
  AiAccess,
  Opportunities,
  OpportunityInsight,
  OpportunityLens,
  PageReadings,
} from "@crux/contracts";
import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Opportunities (DESIGN.md §9, "Opportunities"): the list for a range, one
 * item's answers opened up, and which AI crawlers the brand's site lets in.
 */

const LIST = "/api/workspaces/{workspaceId}/reports/{reportId}/opportunities";
const LENS = "/api/workspaces/{workspaceId}/reports/{reportId}/opportunities/lens";
const ACCESS = "/api/workspaces/{workspaceId}/reports/{reportId}/opportunities/access";
const INSIGHT = "/api/workspaces/{workspaceId}/reports/{reportId}/opportunities/insight";
const READINGS =
  "/api/workspaces/{workspaceId}/reports/{reportId}/opportunities/readings";

export type OpportunitiesQuery = NonNullable<
  paths[typeof LIST]["get"]["parameters"]["query"]
>;
export type OpportunityLensQuery = NonNullable<
  paths[typeof LENS]["get"]["parameters"]["query"]
>;

export async function fetchOpportunities(
  workspaceId: string,
  reportId: string,
  query: OpportunitiesQuery,
): Promise<Opportunities> {
  const { data, response } = await api.GET(LIST, {
    params: { path: { workspaceId, reportId }, query },
  });
  if (data === undefined) {
    throw new Error(problem(response.status, "load these opportunities"));
  }
  return data;
}

export async function fetchOpportunityLens(
  workspaceId: string,
  reportId: string,
  query: OpportunityLensQuery,
): Promise<OpportunityLens> {
  const { data, response } = await api.GET(LENS, {
    params: { path: { workspaceId, reportId }, query },
  });
  if (data === undefined) {
    throw new Error(problem(response.status, "open this opportunity"));
  }
  return data;
}

export async function fetchAiAccess(
  workspaceId: string,
  reportId: string,
): Promise<AiAccess> {
  const { data, response } = await api.GET(ACCESS, {
    params: { path: { workspaceId, reportId } },
  });
  if (data === undefined) {
    throw new Error(problem(response.status, "read your site's robots.txt"));
  }
  return data;
}

/** The same answers as the lens, seen four more ways. */
export async function fetchOpportunityInsight(
  workspaceId: string,
  reportId: string,
  query: OpportunityLensQuery,
): Promise<OpportunityInsight> {
  const { data, response } = await api.GET(INSIGHT, {
    params: { path: { workspaceId, reportId }, query },
  });
  if (data === undefined) {
    throw new Error(problem(response.status, "look into this opportunity"));
  }
  return data;
}

/**
 * The readings of the pages behind an opportunity. Asking puts any page with
 * no reading from the last week on the server's fetch queue, so asking again
 * is how a drawer watches them arrive; it never queues a page twice.
 */
export interface ReadAsk {
  readonly from: string;
  readonly to: string;
  readonly engine?: string;
  readonly urls: readonly string[];
}

export async function readPages(
  workspaceId: string,
  reportId: string,
  ask: ReadAsk,
): Promise<PageReadings> {
  const { data, response } = await api.POST(READINGS, {
    params: { path: { workspaceId, reportId } },
    body: {
      from: ask.from,
      to: ask.to,
      urls: [...ask.urls],
      ...(ask.engine === undefined ? {} : { engine: ask.engine }),
    },
  });
  if (data === undefined) {
    throw new Error(problem(response.status, "read the pages behind this"));
  }
  return data;
}
